/**
 * Is the operator's saved printer really gone, or have we simply not seen the
 * printer list yet?
 *
 * The difference matters because the answer used to delete the saved choice.
 * An empty list is NOT evidence of absence here:
 *  - `DeviceManager.getSystemPrinters()` returns `[]` on any error from
 *    `getPrintersAsync()`, including "the Windows spooler is still starting";
 *  - `useDeviceManager` renders with `printers: []` before the first IPC round
 *    trip has come back, and permanently so on the web/Tauri paths;
 *  - a booth whose USB printer is briefly unplugged enumerates zero printers.
 *
 * In every one of those cases the booth used to forget the printer, and because
 * `handleContinue` refuses to leave Settings without one, the operator was stuck
 * re-picking it — "pengaturan printer dan kamera selalu reset setelah restart".
 *
 * So: only forget a saved printer when we have a real list in hand and the saved
 * name is not in it. Keeping a name that no longer exists is the cheaper mistake
 * — the operator sees it marked as not detected and can pick another one.
 */
export function shouldForgetSavedPrinter(
  savedPrinter: string | undefined,
  availablePrinters: string[] | undefined
): boolean {
  if (!savedPrinter) return false
  if (!availablePrinters || availablePrinters.length === 0) return false
  return !availablePrinters.includes(savedPrinter)
}

/**
 * True when the saved printer should still be offered in the picker even though
 * this machine is not reporting it right now — so the `<select>` shows the
 * booth's actual printer instead of silently falling back to "Default (System
 * Dialog)", which prints to the wrong device.
 */
export function savedPrinterIsMissingFromList(
  savedPrinter: string | undefined,
  availablePrinters: string[] | undefined
): boolean {
  if (!savedPrinter) return false
  return !(availablePrinters ?? []).includes(savedPrinter)
}
